import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
  Alert,
  Pagination,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { softwareAPI, downloadAPI } from '../services/api';
import { toast } from 'react-toastify';

const Home = () => {
  const [software, setSoftware] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });

  const fetchCategories = useCallback(async () => {
    try {
      const res = await softwareAPI.getCategories();
      setCategories(res.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchSoftware = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: 12,
        ...filters
      };
      
      if (filters.category === 'all') {
        delete params.category;
      }
      
      const res = await softwareAPI.getAll(params);
      setSoftware(res.data.data);
      setPagination({
        page: res.data.currentPage,
        totalPages: res.data.totalPages,
        total: res.data.total
      });
    } catch (error) {
      console.error('Error fetching software:', error);
      toast.error('Error al cargar el software');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page]);

  useEffect(() => {
    fetchCategories();
    fetchSoftware();
  }, [fetchCategories, fetchSoftware]);

  const handleFilterChange = (name, value) => {
    setFilters({ ...filters, [name]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSoftware();
  };

  const handleDownload = (id, name) => {
    try {
      downloadAPI.download(id);
      toast.success(`Descargando ${name}...`);
    } catch (error) {
      toast.error('Error al iniciar la descarga');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom align="center">
        Biblioteca de Software
      </Typography>
      
      <Typography variant="h6" color="textSecondary" align="center" paragraph>
        Descarga software gratuito de alta calidad
      </Typography>

      {/* Filtros */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex' }}>
              <TextField
                fullWidth
                placeholder="Buscar software..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <Button type="submit" size="small">
                      <SearchIcon />
                    </Button>
                  )
                }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={filters.category}
                label="Categoría"
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <MenuItem value="all">Todas las categorías</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Ordenar por</InputLabel>
              <Select
                value={filters.sortBy}
                label="Ordenar por"
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                <MenuItem value="createdAt">Más reciente</MenuItem>
                <MenuItem value="downloadCount">Más descargado</MenuItem>
                <MenuItem value="name">Nombre</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Orden</InputLabel>
              <Select
                value={filters.sortOrder}
                label="Orden"
                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              >
                <MenuItem value="desc">Descendente</MenuItem>
                <MenuItem value="asc">Ascendente</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading */}
      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Software Grid */}
      {!loading && (
        <>
          {software.length === 0 ? (
            <Alert severity="info">
              No se encontró software con los filtros seleccionados.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {software.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.id || item._id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography gutterBottom variant="h5" component="h2">
                        {item.name}
                      </Typography>
                      
                      <Typography variant="body2" color="textSecondary" paragraph>
                        {item.description.length > 150 
                          ? `${item.description.substring(0, 150)}...`
                          : item.description
                        }
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Chip 
                          label={`v${item.version}`} 
                          size="small" 
                          color="primary" 
                          sx={{ mr: 1 }}
                        />
                        <Chip 
                          label={categories.find(c => c.value === item.category)?.label || item.category}
                          size="small" 
                          variant="outlined"
                        />
                      </Box>
                      
                      <Typography variant="body2" color="textSecondary">
                        Tamaño: {formatFileSize(item.fileSize)}
                      </Typography>
                      
                      <Typography variant="body2" color="textSecondary">
                        Descargas: {item.downloadCount}
                      </Typography>
                      
                      {item.tags && item.tags.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          {item.tags.slice(0, 3).map((tag, index) => (
                            <Chip 
                              key={index}
                              label={tag} 
                              size="small" 
                              variant="outlined"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      )}
                    </CardContent>
                    
                    <CardActions>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownload(item.id || item._id, item.name)}
                        fullWidth
                      >
                        Descargar
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Paginación */}
          {pagination.totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={(e, page) => setPagination({ ...pagination, page })}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default Home;